import { type VFC, useEffect, useMemo, useState, useRef } from 'react';
import { Drawer, Button, Space, Row, Col, Input, Typography, message, Divider, Switch, Tooltip } from 'antd';
import { useMutation } from '@apollo/client';
import Handlebars from 'handlebars';
import { GET_TEMPLATE_DETAIL, type TemplateDetailData, type TemplateDetailVars } from '../queries/getTemplateDetail';
import { UPDATE_TEMPLATE, type UpdateTemplateData, type UpdateTemplateVars } from '../mutations/updateTemplate';
import { useStrapiQuery } from '../../../../../hooks/useStrapiQuery';
import type { TextAreaRef } from 'antd/lib/input/TextArea';

const { TextArea } = Input;

interface Props {
  open: boolean;
  templateId?: string;
  onClose: () => void;
  onSaved?: () => void;
}

function ensureHelpers() {
  const isOptions = (o: any) =>
    !!o && typeof o === 'object' && typeof o.fn === 'function' && typeof o.inverse === 'function';
  const blockOrInline = (pass: boolean, optionsOrUndefined: any, ctx: any) => {
    if (isOptions(optionsOrUndefined)) {
      return pass ? optionsOrUndefined.fn(ctx) : optionsOrUndefined.inverse(ctx);
    }
    return pass;
  };

  if (!Handlebars.helpers.eq) {
    Handlebars.registerHelper('eq', function (a: any, b: any, options?: any) {
      return blockOrInline(a === b, options, this);
    });
  }
  if (!Handlebars.helpers.ne) {
    Handlebars.registerHelper('ne', function (a: any, b: any, options?: any) {
      return blockOrInline(a !== b, options, this);
    });
  }
  if (!Handlebars.helpers.lt) {
    Handlebars.registerHelper('lt', function (a: any, b: any, options?: any) {
      return blockOrInline(a < b, options, this);
    });
  }
  if (!Handlebars.helpers.lte) {
    Handlebars.registerHelper('lte', function (a: any, b: any, options?: any) {
      return blockOrInline(a <= b, options, this);
    });
  }
  if (!Handlebars.helpers.gt) {
    Handlebars.registerHelper('gt', function (a: any, b: any, options?: any) {
      return blockOrInline(a > b, options, this);
    });
  }
  if (!Handlebars.helpers.gte) {
    Handlebars.registerHelper('gte', function (a: any, b: any, options?: any) {
      return blockOrInline(a >= b, options, this);
    });
  }
  if (!Handlebars.helpers.and) {
    Handlebars.registerHelper('and', function (...args: any[]) {
      const options = args.pop();
      const pass = args.every(Boolean);
      return blockOrInline(pass, options, this);
    });
  }
  if (!Handlebars.helpers.or) {
    Handlebars.registerHelper('or', function (...args: any[]) {
      const options = args.pop();
      const pass = args.some(Boolean);
      return blockOrInline(pass, options, this);
    });
  }
  if (!Handlebars.helpers.not) {
    Handlebars.registerHelper('not', function (a: any, options?: any) {
      return blockOrInline(!a, options, this);
    });
  }
  if (!Handlebars.helpers.includes) {
    Handlebars.registerHelper('includes', function (container: any, item: any, options?: any) {
      const pass = Array.isArray(container)
        ? container.includes(item)
        : typeof container === 'string'
          ? container.includes(String(item))
          : false;
      return blockOrInline(pass, options, this);
    });
  }
  if (!Handlebars.helpers.json) {
    Handlebars.registerHelper('json', (ctx: any) => {
      try {
        return new Handlebars.SafeString(JSON.stringify(ctx, null, 2));
      } catch {
        return '{}';
      }
    });
  }

  if (!Handlebars.helpers.dateIsAfter) {
    Handlebars.registerHelper(
      'dateIsAfter',
      function (this: any, date1: string, date2: string, options?: Handlebars.HelperOptions) {
        const pass = new Date(date1).getTime() > new Date(date2).getTime();
        // Block form
        if (options && typeof options.fn === 'function') {
          if (pass) return options.fn(this);
          return typeof options.inverse === 'function' ? options.inverse(this) : '';
        }
        // Inline form returns boolean
        return pass;
      },
    );
  }

  // buildingFloor: inline helper
  if (!Handlebars.helpers.buildingFloor) {
    Handlebars.registerHelper('buildingFloor', (floor: number) => {
      const sublevel = Math.abs(floor);
      if (floor === 0) return '1.NP';
      return floor < 0 ? `${sublevel}.PP` : `${floor + 1}.NP`;
    });
  }
}

ensureHelpers();

const sharedTextStyle = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: 14,
  lineHeight: 1.5,
};

export const TemplateEditorDrawer: VFC<Props> = ({ open, templateId, onClose, onSaved }) => {
  const [template, setTemplate] = useState('');
  const [name, setName] = useState('');
  const [sampleJson, setSampleJson] = useState(
    '{\n  "meter": { "id": 123, "monitored_entity": "Sample", "type": "electricity" }\n}',
  );
  const [treatAsHbs, setTreatAsHbs] = useState(true);

  const textAreaRef = useRef<TextAreaRef>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);

  const { loading, error } = useStrapiQuery<TemplateDetailData, TemplateDetailVars>(GET_TEMPLATE_DETAIL, {
    variables: templateId ? { id: templateId } : ({} as any),
    skip: !(open && templateId),
    fetchPolicy: 'network-only',
    onCompleted: resp => {
      const templateData = resp.template?.data;
      setTemplate(templateData?.template ?? '');
      setName(templateData?.name ?? '');
    },
  });

  const [updateTemplate, { loading: saving }] = useMutation<UpdateTemplateData, UpdateTemplateVars>(UPDATE_TEMPLATE);

  useEffect(() => {
    if (error) message.error('Failed to load template.');
  }, [error]);

  const compiledHtml = useMemo(() => {
    try {
      const json = sampleJson ? JSON.parse(sampleJson) : {};
      if (treatAsHbs) {
        const compiledTemplate = Handlebars.compile(template || '');
        return compiledTemplate(json);
      }
      return template;
    } catch (e) {
      return `<pre style="color:#a61d24;padding:12px">Preview error: ${(e as Error).message}</pre>`;
    }
  }, [template, sampleJson, treatAsHbs]);

  const handleSave = async () => {
    if (!templateId) return;
    try {
      await updateTemplate({ variables: { id: templateId, data: { template } } });
      message.success('Template saved');
      onSaved?.();
      onClose();
    } catch {
      message.error('Failed to save template');
    }
  };

  // Calculate line numbers
  const lineCount = useMemo(() => {
    return template.split('\n').length;
  }, [template]);

  // Sync scroll between line numbers and textarea
  const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
    if (lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };

  return (
    <Drawer
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button loading={saving} type="primary" onClick={handleSave}>
            Save
          </Button>
        </Space>
      }
      open={open}
      title={name ? `Edit: ${name}` : 'Edit template'}
      width="80vw"
      onClose={onClose}
    >
      <Space className="w-100" direction="vertical" style={{ width: '100%' }}>
        <Row gutter={16}>
          <Col span={12}>
            <Space align="center">
              <Tooltip title="Compile using Handlebars (recommended for .hbs)">
                <span>Handlebars</span>
              </Tooltip>
              <Switch checked={treatAsHbs} checkedChildren="ON" unCheckedChildren="OFF" onChange={setTreatAsHbs} />
            </Space>
            <div style={{ marginTop: 12 }}>
              <Typography.Text type="secondary">Sample Data (JSON for preview)</Typography.Text>
              <TextArea autoSize={{ minRows: 6 }} value={sampleJson} onChange={e => setSampleJson(e.target.value)} />
            </div>
            <Divider />
            <Typography.Text strong>Content</Typography.Text>
            <div
              style={{
                display: 'flex',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                overflow: 'hidden',
                backgroundColor: '#fafafa',
              }}
            >
              <div
                ref={lineNumbersRef}
                style={{
                  ...sharedTextStyle,
                  padding: '11px 8px',
                  backgroundColor: '#f5f5f5',
                  borderRight: '1px solid #d9d9d9',
                  textAlign: 'right',
                  color: '#8c8c8c',
                  userSelect: 'none',
                  overflow: 'hidden',
                  minWidth: '40px',
                  lineHeight: '1.558',
                }}
              >
                {Array.from({ length: lineCount }, (_, i) => (
                  <div key={i + 1}>{i + 1}</div>
                ))}
              </div>
              <TextArea
                ref={textAreaRef}
                autoSize={{ minRows: 18 }}
                disabled={loading}
                style={{
                  ...sharedTextStyle,
                  border: 'none',
                  borderRadius: 0,
                  backgroundColor: 'white',
                  resize: 'none',
                  padding: '11px',
                  flex: 1,
                }}
                value={template}
                onChange={e => setTemplate(e.target.value)}
                onScroll={handleScroll}
              />
            </div>
          </Col>
          <Col span={12}>
            <Typography.Text strong>Preview</Typography.Text>
            <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
              <iframe
                sandbox=""
                srcDoc={compiledHtml}
                style={{ width: '100%', height: 520, border: 0 }}
                title="template-preview"
              />
            </div>
          </Col>
        </Row>
      </Space>
    </Drawer>
  );
};
